import dashify from 'dashify';
import React from 'react';
import { DateTime } from 'luxon';
import styled from '@benzinga/themetron';
import { appEnvironment, formatTwitterLink } from '@benzinga/utils';
import { rgba } from 'polished';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { Icon } from '@benzinga/core-ui';
import { FollowAuthorButton } from './FollowAuthorButton';

const CommentsCount = React.lazy(() =>
  import('@benzinga/comments-ui').then(module => ({ default: module.CommentsCount })),
);

export interface AuthorContentProps {
  authorName: string;
  authorLink?: string;
  authorType: string;
  authorUid?: number;
  className?: string;
  commentCount?: number;
  created: string;
  isDraft?: boolean;
  isPartialView?: boolean;
  readTime: string;
  secondaryByline?: string;
  twitter?: string | null;
  toggleCommentsDrawer?: () => void;
  showCommentButton?: boolean;
  url?: string;
}

const generateAuthorByLine = (author, authorDetailLink, authorType) => {
  if (author === null) return null;

  let authorName = author || 'Benzinga Staff';

  if (authorDetailLink) {
    authorName =
      authorName !== 'Benzinga Staff' ? (
        <a className="author-name" data-action="Clicked Author Link" href={authorDetailLink}>
          {authorName}
        </a>
      ) : (
        'Benzinga Staff'
      );
  }

  let authorByLine = '';
  if (authorType) {
    authorByLine = `, ${authorType}`;
  }

  return (
    <>
      {authorName}
      {authorByLine}
    </>
  );
};

export const AuthorContent: React.FC<AuthorContentProps> = React.memo(
  ({
    authorLink,
    authorName,
    authorType,
    authorUid,
    className = '',
    commentCount,
    created,
    isDraft,
    isPartialView,
    readTime,
    secondaryByline,
    showCommentButton = false,
    toggleCommentsDrawer,
    twitter,
    url,
  }) => {
    const authorDetailLink =
      authorLink || (authorName?.trim() ? `${process.env.BASE_URL}/author/${dashify(authorName.toLowerCase())}` : null);
    const articleCreationTime = created
      ? DateTime.fromISO(created, { setZone: true })
          .setZone(appEnvironment().config().zone)
          .toFormat('LLLL d, yyyy h:mm a')
      : null;

    return (
      <Container className={`article-submitted ${className}`}>
        <div className="article-submitted-content">
          <div>
            <div className="article-author-wrap">
              {authorName !== null && <span>by </span>}
              <span className="article-author">
                {authorName !== null && generateAuthorByLine(authorName, authorDetailLink, authorType)}
              </span>
              {authorUid && <FollowAuthorButton authorName={authorName} authorUid={authorUid} />}
              <a
                aria-label="Find on Twitter"
                className="twitter-link"
                href={formatTwitterLink(twitter || 'benzinga')}
                rel="noreferrer"
                target="_blank"
              >
                <Icon icon={faXTwitter} />
              </a>
            </div>
            <div>
              <span className="secondary-byline">{secondaryByline}</span>
            </div>
          </div>
          <div className="article-date-wrap">
            {isDraft && <span className="draft-badge">Draft</span>}{' '}
            <span className="article-date">{articleCreationTime}</span>{' '}
            {articleCreationTime && <span>| {readTime} </span>}
            {showCommentButton && (
              <>
                {' | '}
                <CommentsCount
                  count={commentCount}
                  onClick={toggleCommentsDrawer}
                  url={isPartialView ? url : undefined}
                />
              </>
            )}
          </div>
        </div>
      </Container>
    );
  },
);

const Container = styled.div`
  &.article-submitted {
    .article-submitted-content {
      font-size: ${({ theme }) => theme.fontSize.sm};
      flex-wrap: wrap;
    }

    .article-date-wrap {
      color: ${({ theme }) => rgba(theme.colorPalette.gray500, 0.8)};
    }

    .draft-badge {
      background-color: ${({ theme }) => theme.colorPalette.blue200};
      padding: 2px 10px;
      border-radius: ${({ theme }) => theme.borderRadius.default};
    }

    .separator {
      border-left: 1px solid #ccc;
      display: inline-block;
      font-size: 0.4rem;
      margin: 0 4px;
    }

    .article-author-wrap {
      align-items: center;
      display: inline-flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .twitter-link {
      color: white;
      background-color: black;
      width: 20px;
      height: 20px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media screen and (min-width: 400px) {
      .article-submitted-content {
        display: flex;
        line-height: 1.8;
        align-items: flex-start;

        .article-author-wrap {
          white-space: nowrap;

          &:after {
            content: '';
            border-right: 1px solid #555;
            padding-right: 10px;
            margin-right: 10px;
          }
        }
      }
    }
    @media screen and (min-width: 800px) {
      .article-submitted-content {
        font-size: ${({ theme }) => theme.fontSize.base};
        display: block;
        padding-top: 0.5rem;
        .article-author-wrap {
          line-height: unset;
          &:after {
            content: none;
          }
        }
      }
    }
  }
`;
